import React, { useState, useEffect, useMemo } from "react";

// MUI
import HistoryIcon from "@mui/icons-material/History";
import IconButton from "@mui/material/IconButton";

// Hooks
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";

// Utils
import {
  isValidArr,
  displayDateFormart,
  getProjectStatus,
  showThousandSuperator,
  trimEllip,
} from "../../utility/utils";

// Config
import {
  projectBidStatusObj,
  approvedWinLossBidStatuses,
} from "../../utility/config";

// Components
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import Loader from "../../components/loader/Loader";
import LongMsg from "../../components/LongMsg";
import DrawerComponent from "../../components/DrawerComponent";
import WinLossSection from "../../components/project-details/WinLossSection";
import ProjectHistory from "../project/ProjectHistory";

// Actions
import { fetchAllProjectsWithStatus } from "../../redux/actions/ProjectResponseAction";

type iData = {
  id: number;
  projectName: any;
  projectNameStr: string;
  estimatedValue: string;
  bidStatus: string;
  bidStatusKey: string;
  bidSubmissionDate: string;
  tenderMaturity: string;
  history: any;
};

type EditData = {
  projectId: number;
  projectStatus: string;
  tenderMaturity: string;
};

type ProjectHistoryObj = { id: number; name: string };

const ProjectsWinLossPage = () => {
  const dispatch = useAppDispatch();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editData, setEditData] = useState<EditData | null>(null);
  const [historyProject, setHistoryProject] =
    useState<ProjectHistoryObj | null>(null);

  const allProjects = useAppSelector(
    (state) => state.projectResponse.project_Response
  );

  const isFetching = useAppSelector(
    (state) => state.projectResponse.isFetching
  );

  // Columns data
  const columns = useMemo(() => {
    return [
      {
        id: "id",
        numeric: false,
        disablePadding: false,
        label: "Project ID",
        width: 100,
      },
      {
        id: "projectName",
        numeric: false,
        disablePadding: false,
        label: "Project Name",
        width: 200,
      },
      {
        id: "estimatedValue",
        numeric: false,
        disablePadding: false,
        label: "Estimated Value (Cr)",
        width: 180,
      },
      {
        id: "bidStatus",
        numeric: false,
        disablePadding: false,
        label: "Bid Status",
        width: 150,
      },
      {
        id: "bidSubmissionDate",
        numeric: false,
        disablePadding: false,
        label: "Bid Submission Date",
        width: 180,
      },
      {
        id: "history",
        numeric: false,
        disablePadding: false,
        label: "History",
        width: 100,
      },
    ];
  }, []);

  // To fetch all the projects
  useEffect(() => {
    dispatch(fetchAllProjectsWithStatus());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    const handleProjectHistory = (id: number, name: string) => {
      setHistoryProject({
        id: id,
        name: name,
      });
      setIsDrawerOpen(true);
      setEditData(null);
    };

    setRootData(() => {
      const result: iData[] = [];
      allProjects.forEach((project) => {
        const res = getProjectStatus(project);
        if (
          res.key === projectBidStatusObj.rejected ||
          approvedWinLossBidStatuses.includes(res.key)
        ) {
          result.push({
            id: project.projectId,
            projectName: (
              <LongMsg
                msg={project.projectName}
                limit={25}
                isExploratory={project.bidExploratoryStatus}
                isDropped={project.isBidDropped}
                priority={project.priority}
              />
            ),
            projectNameStr: project.projectName,
            estimatedValue: showThousandSuperator(project.estimatedValue),
            bidStatus: res.value,
            bidStatusKey: res.key,
            bidSubmissionDate: displayDateFormart(project.bidSubmissionDate),
            tenderMaturity: project.tenderMaturity || "",
            history: (
              <IconButton
                aria-label="history"
                onClick={() =>
                  handleProjectHistory(
                    project.projectId,
                    trimEllip(project.projectName, 30)
                  )
                }
              >
                <HistoryIcon />
              </IconButton>
            ),
          });
        }
      });
      return result;
    });
  }, [allProjects]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = rootData.find((rec) => rec.id === id);
    if (editRec) {
      setEditData(() => ({
        projectId: editRec.id,
        projectStatus:
          editRec.bidStatusKey === projectBidStatusObj.rejected
            ? projectBidStatusObj.rejected
            : projectBidStatusObj.approved,
        tenderMaturity: editRec.tenderMaturity,
      }));
      setHistoryProject(null);
      setIsDrawerOpen(true);
    }
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      {!isValidArr(allProjects) && isFetching ? <Loader /> : null}
      <PageTitle title="Projects Bid Status" />
      <PageSearch placeholder="Search Projects" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isDrawerOpen ? (
        <DrawerComponent
          open={isDrawerOpen}
          title={
            historyProject ? `${historyProject.name} History` : "Bid Win Loss"
          }
          onClose={handleDrawerClose}
        >
          {historyProject ? (
            <ProjectHistory
              projectId={historyProject.id}
              type="ProjectBidderDetails"
            />
          ) : editData ? (
            <WinLossSection
              projectId={editData.projectId}
              projectStatus={editData.projectStatus}
              tenderMaturity={editData.tenderMaturity}
            />
          ) : null}
        </DrawerComponent>
      ) : null}
    </>
  );
};

export default ProjectsWinLossPage;
